/**
 * YarnTestEntryPage — Phase 3A keystone (Sec 30 + Sec 24.8)
 * ─────────────────────────────────────────────────────────────────────────────
 * Manual data entry form for yarn QC test results. Lab tech captures CSP /
 * Uster / classimat / breaks / realization / customer feedback, system
 * computes composite quality_score and A/B/C/D performance grade, and
 * stamps the result against the FG lot or laydown plan for traceability.
 *
 * The score+grade is computed on the SERVER (yarn_test_service.compute_quality_score)
 * so the same weighting applies across the API and any future CSV import.
 *
 * Spec source: cotton mixing standards master, Sec 30 (yarn feedback loop).
 */
import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, DatePicker, Form, Input, InputNumber,
  Row, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd'
import {
  ExperimentOutlined, ReloadOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { yarnTestApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'

const { Text } = Typography

type Plant = { id: string; name: string }

type YarnTest = {
  id: string
  test_date: string
  count_ne: number
  yarn_type: string | null
  csp: number | null
  u_pct: number | null
  neps_per_km: number | null
  hairiness: number | null
  end_breaks_per_1000_spdl_hr: number | null
  realization_pct: number | null
  customer_acceptance: string | null
  quality_score: number | null
  performance_grade: string | null
  fg_lot_id: string | null
  laydown_plan_id: string | null
}

const GRADE_COLOR: Record<string, string> = {
  A: '#52c41a', B: '#1677ff', C: '#faad14', D: '#ff4d4f',
}

const ACCEPTANCE_TAG: Record<string, string> = {
  ACCEPTED: 'green',
  COMPLAINT: 'orange',
  RETURNED: 'red',
  PENDING: 'default',
}

export default function YarnTestEntryPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [days, setDays] = useState<number>(30)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const plantsQ = useQuery<Plant[]>({
    queryKey: ['plants', 'all'],
    queryFn: async () => {
      const res = await plantsApi.list()
      const r = res as unknown as { items?: Plant[]; data?: Plant[] } | Plant[]
      if (Array.isArray(r)) return r
      return r.items ?? r.data ?? []
    },
  })

  if (!plantId && plantsQ.data && plantsQ.data.length > 0) {
    setPlantId(plantsQ.data[0].id)
  }

  const testsQ = useQuery<YarnTest[]>({
    queryKey: ['yarn-tests', plantId, days],
    queryFn: () => yarnTestApi.list({ plant_id: plantId, days, limit: 50 }),
    enabled: Boolean(plantId),
  })

  const create = useMutation({
    mutationFn: (data: Record<string, unknown>) => yarnTestApi.create(data),
    onSuccess: (test) => {
      message.success(
        `Test saved. Quality score: ${test.quality_score} (Grade ${test.performance_grade})`,
        6,
      )
      qc.invalidateQueries({ queryKey: ['yarn-tests'] })
      form.resetFields()
      form.setFieldsValue({ plant_id: plantId, test_date: dayjs() })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const submit = () => {
    form.validateFields().then((values) => {
      setSubmitting(true)
      const payload: Record<string, unknown> = {
        ...values,
        plant_id: plantId,
        test_date: values.test_date?.format('YYYY-MM-DD'),
      }
      // Strip empty optional links
      if (!payload.fg_lot_id) delete payload.fg_lot_id
      if (!payload.laydown_plan_id) delete payload.laydown_plan_id
      if (!payload.bale_selection_run_id) delete payload.bale_selection_run_id
      create.mutate(payload, { onSettled: () => setSubmitting(false) })
    })
  }

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Yarn Test Entry"
        subtitle="Lab tech enters CSP / Uster / breaks / realization → system scores 0-100 and grades A/B/C/D"
        extra={
          <Space>
            <Select
              value={plantId} onChange={setPlantId}
              placeholder="Plant" style={{ width: 180 }}
              options={(plantsQ.data || []).map(p => ({ label: p.name, value: p.id }))}
            />
            <Tooltip title="Refresh"><ReloadOutlined
              onClick={() => testsQ.refetch()}
              style={{ fontSize: 16, cursor: 'pointer' }}
              spin={testsQ.isFetching}
            /></Tooltip>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        icon={<ExperimentOutlined />}
        style={{ marginBottom: 16 }}
        message="Score weighting (0-100 scale)"
        description={
          <span style={{ fontSize: 12 }}>
            CSP 30pts (1500=full, 1000=0) · U% 20pts (8%=full, 14%=0) · Neps 15pts (50=full, 250=0) · Hairiness 10pts (4=full, 7=0) · End breaks 10pts (3=full, 15=0) · Realization 15pts (90%=full, 78%=0). Customer COMPLAINT −20pts. RETURNED caps to 30.
          </span>
        }
      />

      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card title="New test entry" styles={{ body: { padding: 16 } }}>
            <Form form={form} layout="vertical" initialValues={{
              test_date: dayjs(),
              count_ne: 30,
            }}>
              <Row gutter={12}>
                <Col xs={12} md={8}>
                  <Form.Item label="Test date" name="test_date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Count (Ne)" name="count_ne" rules={[{ required: true }]}>
                    <InputNumber min={4} max={200} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Yarn type" name="yarn_type">
                    <Select allowClear options={[
                      { label: 'Carded', value: 'CARDED' },
                      { label: 'Combed', value: 'COMBED' },
                      { label: 'Compact', value: 'COMPACT' },
                      { label: 'Carded compact', value: 'CARDED_COMPACT' },
                      { label: 'Combed compact', value: 'COMBED_COMPACT' },
                      { label: 'Siro', value: 'SIRO' },
                      { label: 'OE', value: 'OE' },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="FG Lot ID (or Laydown Plan ID for trial)"
                name="fg_lot_id"
                extra="At least one of FG Lot / Laydown / Run must be set">
                <Input placeholder="UUID of fg_lots row" />
              </Form.Item>
              <Form.Item label="Laydown Plan ID (optional)" name="laydown_plan_id">
                <Input placeholder="UUID of laydown_plans row" />
              </Form.Item>
              <Form.Item label="Bale Selection Run ID (optional)" name="bale_selection_run_id">
                <Input placeholder="UUID of bale_selection_runs row" />
              </Form.Item>

              <Text strong>CSP & Uster</Text>
              <Row gutter={12} style={{ marginTop: 8 }}>
                <Col xs={12} md={6}>
                  <Form.Item label="CSP" name="csp">
                    <InputNumber min={500} max={3000} step={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="U%" name="u_pct">
                    <InputNumber min={5} max={20} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="CV_m %" name="cv_m_pct">
                    <InputNumber min={5} max={25} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Hairiness" name="hairiness">
                    <InputNumber min={2} max={10} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Thin / km" name="thin_per_km">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Thick / km" name="thick_per_km">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Neps / km" name="neps_per_km">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Classimat / 100km" name="classimat_per_100km">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Text strong>Process performance</Text>
              <Row gutter={12} style={{ marginTop: 8 }}>
                <Col xs={12} md={8}>
                  <Form.Item label="End breaks / 1000 spdl-hr" name="end_breaks_per_1000_spdl_hr">
                    <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Winding cuts / 100km" name="winding_cuts_per_100km">
                    <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Count CV %" name="count_cv_pct">
                    <InputNumber min={0} max={5} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Text strong>Realization (auto-computes if input + output set)</Text>
              <Row gutter={12} style={{ marginTop: 8 }}>
                <Col xs={12} md={6}>
                  <Form.Item label="Cotton input (kg)" name="cotton_input_kg">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Yarn output (kg)" name="yarn_output_kg">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Waste (kg)" name="waste_kg">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Realization %" name="realization_pct">
                    <InputNumber min={50} max={100} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Text strong>Customer feedback</Text>
              <Row gutter={12} style={{ marginTop: 8 }}>
                <Col xs={12} md={6}>
                  <Form.Item label="Acceptance" name="customer_acceptance">
                    <Select allowClear options={[
                      { label: 'Accepted', value: 'ACCEPTED' },
                      { label: 'Complaint (-20 pts)', value: 'COMPLAINT' },
                      { label: 'Returned (caps to 30)', value: 'RETURNED' },
                      { label: 'Pending', value: 'PENDING' },
                    ]} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={18}>
                  <Form.Item label="Complaint detail" name="complaint_text">
                    <Input.TextArea rows={1} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Notes" name="notes">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>

              <Button type="primary" block size="large" onClick={submit} loading={submitting || create.isPending}>
                Save & score
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title={`Recent tests (last ${days} days)`} styles={{ body: { padding: 0 } }}>
            <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0' }}>
              <Select size="small" value={days} onChange={setDays} style={{ width: '100%' }}
                options={[
                  { label: 'Last 7 days', value: 7 },
                  { label: 'Last 30 days', value: 30 },
                  { label: 'Last 90 days', value: 90 },
                ]} />
            </div>
            <Table
              rowKey="id"
              size="small"
              loading={testsQ.isLoading}
              dataSource={testsQ.data || []}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              columns={[
                { title: 'Date', dataIndex: 'test_date', width: 100,
                  render: (v) => dayjs(v).format('DD MMM') },
                { title: 'Count', dataIndex: 'count_ne', width: 60, align: 'center',
                  render: (v) => v ? <Tag>{v}s</Tag> : '—' },
                { title: 'CSP', dataIndex: 'csp', width: 70, align: 'right',
                  render: (v) => v != null ? Number(v).toFixed(0) : '—' },
                { title: 'U%', dataIndex: 'u_pct', width: 60, align: 'right',
                  render: (v) => v != null ? Number(v).toFixed(1) : '—' },
                { title: 'Score', dataIndex: 'quality_score', width: 80, align: 'center',
                  render: (v, r) => v != null
                    ? <Tag color={GRADE_COLOR[r.performance_grade ?? 'D']} style={{ color: '#fff' }}>
                        {Number(v).toFixed(0)} {r.performance_grade}
                      </Tag>
                    : '—' },
                { title: '', width: 80, render: (_, r) =>
                  r.customer_acceptance && (
                    <Tag color={ACCEPTANCE_TAG[r.customer_acceptance]}>
                      {r.customer_acceptance.slice(0, 3)}
                    </Tag>
                  ) },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
